<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import { useProcurementsStore } from './stores/procurements';
import { useCountriesStore } from './stores/countries';
import { useCitiesStore } from './stores/cities';
import { useProcurementTypesStore } from './stores/procurementTypes';
import { useBusinessModelTypologiesStore } from './stores/businessModelTypologies';
import { useCityPopulationRangesStore } from './stores/cityPopulationRanges';
import apiFetch from '@wordpress/api-fetch';
import SelectIcon from './assets/select-icon.svg?component';
import CityIcon from './assets/city-icon.svg?component';
import CountryIcon from './assets/country-icon.svg?component';
import YearIcon from './assets/year-icon.svg?component';
import ProcurementIcon from './assets/procurement-icon.svg?component';
import BusinessIcon from './assets/business-icon-2.svg?component';
import ArrowIcon from './assets/arrow.svg?component';
import InfoIcon from './assets/info-icon.svg?component';
import { useRouteLoadingStore } from './stores/routeLoading';

const routeLoadingStore = useRouteLoadingStore();
const procurementsStore = useProcurementsStore();
const countriesStore = useCountriesStore();
const citiesStore = useCitiesStore();
const procurementTypesStore = useProcurementTypesStore();
const businessModelTypologiesStore = useBusinessModelTypologiesStore();
const cityPopulationRangesStore = useCityPopulationRangesStore();

const sort = ref({
  field: null,
  order: null,
});
const selectedProcurements = ref([]);
const isLoading = ref(false);
const activeTaxonomy = ref(null);
const isMobileFiltersOpen = ref(false);
const isMobileSortOpen = ref(false);
const openMobileInfoTooltip = ref(null);
const currentPage = ref(1);
const rowsPerPage = 20;
const strings = ref(null);

const selectedIds = computed(() => selectedProcurements.value.join(','));

const lang = document.documentElement.lang?.substring(0, 2);

const { procurementUrls, comparisonPageUrl } = window.marketToolData ?? {};

function withLang(url) {
  if (!url) return '#';
  const match = window.location.pathname.match(/^\/([a-z]{2})\//);
  if (!match) return url;
  return url.replace(/^(https?:\/\/[^/]+)\//, `$1/${match[1]}/`);
}

function getProcurementUrl(procurement) {
  const id = getProcurementId(procurement);
  return withLang(procurementUrls?.[id] ?? '#');
}

function setSort(field, order) {
  sort.value.field = field;
  sort.value.order = order;
}

function getProcurementId(procurement) {
  return procurement?.procurement_id ?? procurement?.id ?? null;
}

function getProcurementValue(procurement, field) {
  switch (field) {
    case 'id':
      return getProcurementId(procurement) || '-';
    case 'city':
      return procurement.city?.name || '-';
    case 'country':
      return procurement.country?.name || '-';
    case 'year':
      return procurement.year ? procurement.year : '-';
    case 'procurement_type':
      return procurement.procurement_type?.name || '-';
    case 'business_model_typology':
      return procurement.business_model_typology?.name || '-';
    default:
      return '';
  }
}

function getProcurementDescription(procurement, field) {
  if (field === 'procurement_type') {
    return procurement.procurement_type?.description;
  }

  if (field === 'business_model_typology') {
    return procurement.business_model_typology?.description;
  }

  return '';
}

const sortedProcurements = computed(() => {
  if (!sort.value.field) {
    return procurementsStore.procurements;
  }

  return [...procurementsStore.procurements].sort((a, b) => {
    const aVal = getProcurementValue(a, sort.value.field).toLowerCase();
    const bVal = getProcurementValue(b, sort.value.field).toLowerCase();

    return sort.value.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
});

const totalPages = computed(() => Math.max(1, Math.ceil(sortedProcurements.value.length / rowsPerPage)));
const paginatedProcurements = computed(() => {
  const start = (currentPage.value - 1) * rowsPerPage;

  return sortedProcurements.value.slice(start, start + rowsPerPage);
});

const canGoPreviousPage = computed(() => currentPage.value > 1);
const canGoNextPage = computed(() => currentPage.value < totalPages.value);

function goToPreviousPage() {
  if (canGoPreviousPage.value) {
    currentPage.value -= 1;
  }
}

function goToNextPage() {
  if (canGoNextPage.value) {
    currentPage.value += 1;
  }
}

function toggleTaxonomy(taxonomy) {
  activeTaxonomy.value = activeTaxonomy.value === taxonomy ? null : taxonomy;
}

function toggleMobileInfoTooltip(id) {
  openMobileInfoTooltip.value = openMobileInfoTooltip.value === id ? null : id;
}

function toggleMobileFilters() {
  isMobileFiltersOpen.value = !isMobileFiltersOpen.value;

  if (isMobileFiltersOpen.value) {
    isMobileSortOpen.value = false;
  } else {
    activeTaxonomy.value = null;
  }
}

function toggleMobileSort() {
  isMobileSortOpen.value = !isMobileSortOpen.value;

  if (isMobileSortOpen.value) {
    isMobileFiltersOpen.value = false;
    activeTaxonomy.value = null;
  }
}

onMounted(async () => {
  try {
    const translations = await apiFetch({
      path: `/wp-json/ebus/market/translations?lang=${lang}`,
    });

    strings.value = translations;

    await procurementsStore.loadProcurements({ lang });
  } catch (error) {
    console.error('Error: ', error);
  } finally {
    routeLoadingStore.stop();
  }

  countriesStore.loadCountries({ lang });
  citiesStore.loadCities({ lang });
  cityPopulationRangesStore.loadRanges({ lang });
  procurementTypesStore.loadProcurementTypes({ lang });
  businessModelTypologiesStore.loadBusinessModelTypologies({ lang });
});

watch(() => [countriesStore.activeCountries, citiesStore.activeCities, cityPopulationRangesStore.activeRanges, procurementTypesStore.activeProcurementTypes, businessModelTypologiesStore.activeBusinessModelTypologies], async () => {
  isLoading.value = true;
  currentPage.value = 1;

    try {
      await procurementsStore.loadProcurements({
        lang,
        country: countriesStore.activeCountries,
        city: citiesStore.activeCities,
        city_population_range: cityPopulationRangesStore.activeRanges,
        procurement_type: procurementTypesStore.activeProcurementTypes,
        business_model_typology: businessModelTypologiesStore.activeBusinessModelTypologies,
      });
    } catch (error) {
      console.error('Error: ', error);
    } finally {
      isLoading.value = false;
    }
  }, { deep: true });

watch(() => sort.value, () => {
  currentPage.value = 1;
}, { deep: true });

watch(() => sortedProcurements.value.length, () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = totalPages.value;
  }
});

const headers = computed(() => {
  return [
    { key: 'select', label: strings.value.select, sortable: false, icon: SelectIcon },
    { key: 'id', label: strings.value.id, sortable: true },
    { key: 'city', label: strings.value.city, sortable: true, icon: CityIcon },
    { key: 'country', label: strings.value.country, sortable: true, icon: CountryIcon },
    { key: 'year', label: strings.value.year, sortable: true, icon: YearIcon },
    { key: 'procurement_type', label: strings.value.procurement_type, sortable: true, icon: ProcurementIcon },
    { key: 'business_model_typology', label: strings.value.business_model, sortable: true, icon: BusinessIcon },
  ];
});

const mobileCardHeaders = computed(() => (
  headers.value.filter(({ key }) => key !== 'select' && key !== 'id')
));

const mobileSortableHeaders = computed(() => (
  headers.value.filter(({ sortable, key }) => sortable && key !== 'id')
));
</script>

<template>
  <section v-if="strings" class="relative! w-full! min-h-screen! flex! flex-col! gap-y-6! p-6! bg-light-blue-3!">
    <div class="flex! flex-col! gap-y-6! lg:gap-y-8! bg-light-blue-2! -mx-6! -mt-6! px-6! pt-6! lg:px-6! lg:pt-6! lg:pb-6! lg:-m-6!">
      <h2 class="font-ibm-plex-mono! font-medium! text-4xl! leading-tight! text-black!">
        {{ strings.procurements }}
      </h2>

      <div class="hidden! lg:flex! flex-col! gap-y-2!">
        <div class="font-ibm-plex-mono! font-medium! text-lg! leading-tight! text-black!">
          {{ strings.filter_by }}
        </div>

        <div class="flex! flex-col! gap-y-3.5!">
          <div class="flex! flex-wrap! items-center! gap-8!">
            <div
              class="flex! items-center! gap-2! font-ibm-plex-mono! text-base! leading-tight! text-blue!"
              :class="countriesStore.activeCountries.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              {{ strings.country }}
              <span v-if="countriesStore.activeCountries.length > 0">[{{ countriesStore.activeCountries.length }}]</span>
              <button
                class="w-5! h-5! text-2xl! leading-none! flex! items-center! justify-center!"
                :class="[
                  activeTaxonomy === 'country' ? 'bg-blue! text-white! border! border-blue!' : 'border! border-blue! text-blue!',
                  countriesStore.activeCountries.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('country')"
              >
                {{ activeTaxonomy === 'country' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="flex! items-center! gap-2! font-ibm-plex-mono! text-base! leading-tight! text-blue!"
              :class="citiesStore.activeCities.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              {{ strings.city_region }}
              <span v-if="citiesStore.activeCities.length > 0">[{{ citiesStore.activeCities.length }}]</span>
              <button
                class="w-5! h-5! text-2xl! leading-none! flex! items-center! justify-center!"
                :class="[
                  activeTaxonomy === 'city' ? 'bg-blue! text-white! border! border-blue!' : 'border! border-blue! text-blue!',
                  citiesStore.activeCities.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('city')"
              >
                {{ activeTaxonomy === 'city' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="flex! items-center! gap-2! font-ibm-plex-mono! text-base! leading-tight! text-blue!"
              :class="cityPopulationRangesStore.activeRanges.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              {{ strings.city_population_range }}
              <span v-if="cityPopulationRangesStore.activeRanges.length > 0">[{{ cityPopulationRangesStore.activeRanges.length }}]</span>
              <button
                class="w-5! h-5! text-2xl! leading-none! flex! items-center! justify-center!"
                :class="[
                  activeTaxonomy === 'city_population_range' ? 'bg-blue! text-white! border! border-blue!' : 'border! border-blue! text-blue!',
                  cityPopulationRangesStore.activeRanges.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('city_population_range')"
              >
                {{ activeTaxonomy === 'city_population_range' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="flex! items-center! gap-2! font-ibm-plex-mono! text-base! leading-tight! text-blue!"
              :class="procurementTypesStore.activeProcurementTypes.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              {{ strings.procurement_type }}
              <span v-if="procurementTypesStore.activeProcurementTypes.length > 0">[{{ procurementTypesStore.activeProcurementTypes.length }}]</span>
              <button
                class="w-5! h-5! text-2xl! leading-none! flex! items-center! justify-center!"
                :class="[
                  activeTaxonomy === 'procurement_type' ? 'bg-blue! text-white! border! border-blue!' : 'border! border-blue! text-blue!',
                  procurementTypesStore.activeProcurementTypes.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('procurement_type')"
              >
                {{ activeTaxonomy === 'procurement_type' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="flex! items-center! gap-2! font-ibm-plex-mono! text-base! leading-tight! text-blue!"
              :class="businessModelTypologiesStore.activeBusinessModelTypologies.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              {{ strings.business_model_typology }}
              <span v-if="businessModelTypologiesStore.activeBusinessModelTypologies.length > 0">[{{ businessModelTypologiesStore.activeBusinessModelTypologies.length }}]</span>
              <button
                class="w-5! h-5! text-2xl! leading-none! flex! items-center! justify-center!"
                :class="[
                  activeTaxonomy === 'business_model_typology' ? 'bg-blue! text-white! border! border-blue!' : 'border! border-blue! text-blue!',
                  businessModelTypologiesStore.activeBusinessModelTypologies.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('business_model_typology')"
              >
                {{ activeTaxonomy === 'business_model_typology' ? '-' : '+' }}
              </button>
            </div>

          </div>

          <div v-if="activeTaxonomy === 'country'" class="flex! flex-wrap! items-center! gap-3.5!">
            <button
              v-for="country in countriesStore.countries"
              :key="country.id"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-3.5! py-0.5! border! border-black! rounded-full!"
              :class="countriesStore.activeCountries.includes(country.code) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="countriesStore.toggleCountry(country.code)"
            >
              {{ country.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'city'" class="flex! flex-wrap! items-center! gap-3.5!">
            <button
              v-for="city in citiesStore.cities"
              :key="city.id"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-3.5! py-0.5! border! border-black! rounded-full!"
              :class="citiesStore.activeCities.includes(city.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="citiesStore.toggleCity(city.slug)"
            >
              {{ city.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'city_population_range'" class="flex! flex-wrap! items-center! gap-3.5!">
            <button
              v-for="range in cityPopulationRangesStore.ranges"
              :key="range.slug"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-3.5! py-0.5! border! border-black! rounded-full!"
              :class="cityPopulationRangesStore.activeRanges.includes(range.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="cityPopulationRangesStore.toggleRange(range.slug)"
            >
              {{ range.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'procurement_type'" class="flex! flex-wrap! items-center! gap-3.5!">
            <button
              v-for="procurementType in procurementTypesStore.procurementTypes"
              :key="procurementType.id"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-3.5! py-0.5! border! border-black! rounded-full!"
              :class="procurementTypesStore.activeProcurementTypes.includes(procurementType.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="procurementTypesStore.toggleProcurementType(procurementType.slug)"
            >
              {{ procurementType.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'business_model_typology'" class="flex! flex-wrap! items-center! gap-3.5!">
            <button
              v-for="businessModelTypology in businessModelTypologiesStore.businessModelTypologies"
              :key="businessModelTypology.id"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-3.5! py-0.5! border! border-black! rounded-full!"
              :class="businessModelTypologiesStore.activeBusinessModelTypologies.includes(businessModelTypology.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="businessModelTypologiesStore.toggleBusinessModelTypology(businessModelTypology.slug)"
            >
              {{ businessModelTypology.name }}
            </button>
          </div>
        </div>
      </div>

      <div class="lg:hidden! flex! flex-col! gap-y-3!">
        <div
          class="flex! items-center! gap-6! bg-light-blue-2! -mx-6! px-6!"
          :class="isMobileFiltersOpen || isMobileSortOpen ? 'pb-0!' : 'pb-6!'"
        >
          <button
            type="button"
            class="font-ibm-plex-mono! font-normal! text-lg! leading-tight! border! rounded-full! px-4! py-0.5! flex! items-center! gap-3!"
            :class="isMobileFiltersOpen ? 'text-white! bg-blue! border-blue!' : 'text-black! bg-transparent! border-black!'"
            @click="toggleMobileFilters"
          >
            {{ strings.filters }}

            <span>{{ isMobileFiltersOpen ? '-' : '+' }}</span>
          </button>

          <button
            type="button"
            class="font-ibm-plex-mono! font-normal! text-lg! leading-tight! border! rounded-full! px-4! py-0.5! flex! items-center! gap-3!"
            :class="isMobileSortOpen ? 'text-white! bg-blue! border-blue!' : 'text-black! bg-transparent! border-black!'"
            @click="toggleMobileSort"
          >
            {{ strings.sort_by }} 

            <span>{{ isMobileSortOpen ? '-' : '+' }}</span>
          </button>
        </div>

        <div v-if="isMobileFiltersOpen" class="flex! flex-col! -mx-6!">
          <div class="flex! flex-col! gap-y-4! bg-light-blue-3! p-6!">
            <div
              class="inline-flex! items-center! gap-1.5! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-blue!"
              :class="countriesStore.activeCountries.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              <span>
                {{ strings.country }}
                <span v-if="countriesStore.activeCountries.length > 0">[{{ countriesStore.activeCountries.length }}]</span>
              </span>
              <button
                class="w-5! h-5! text-xl! leading-none! flex! items-center! justify-center! shrink-0! border!"
                :class="[
                  activeTaxonomy === 'country' ? 'bg-blue! text-white! border-blue!' : 'border-blue! text-blue!',
                  countriesStore.activeCountries.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('country')"
              >
                {{ activeTaxonomy === 'country' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="inline-flex! items-center! gap-1.5! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-blue!"
              :class="citiesStore.activeCities.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              <span>
                {{ strings.city_region }}
                <span v-if="citiesStore.activeCities.length > 0">[{{ citiesStore.activeCities.length }}]</span>
              </span>
              <button
                class="w-4.5! h-4.5! text-xl! leading-none! flex! items-center! justify-center! shrink-0! border!"
                :class="[
                  activeTaxonomy === 'city' ? 'bg-blue! text-white! border-blue!' : 'border-blue! text-blue!',
                  citiesStore.activeCities.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('city')"
              >
                {{ activeTaxonomy === 'city' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="inline-flex! items-center! gap-1.5! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-blue!"
              :class="cityPopulationRangesStore.activeRanges.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              <span>
                {{ strings.city_population_range }}
                <span v-if="cityPopulationRangesStore.activeRanges.length > 0">[{{ cityPopulationRangesStore.activeRanges.length }}]</span>
              </span>
              <button
                class="w-4.5! h-4.5! text-xl! leading-none! flex! items-center! justify-center! shrink-0! border!"
                :class="[
                  activeTaxonomy === 'city_population_range' ? 'bg-blue! text-white! border-blue!' : 'border-blue! text-blue!',
                  cityPopulationRangesStore.activeRanges.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('city_population_range')"
              >
                {{ activeTaxonomy === 'city_population_range' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="inline-flex! items-center! gap-1.5! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-blue!"
              :class="procurementTypesStore.activeProcurementTypes.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              <span>
                {{ strings.procurement_type }}
                <span v-if="procurementTypesStore.activeProcurementTypes.length > 0">[{{ procurementTypesStore.activeProcurementTypes.length }}]</span>
              </span>
              <button
                class="w-4.5! h-4.5! text-xl! leading-none! flex! items-center! justify-center! shrink-0! border!"
                :class="[
                  activeTaxonomy === 'procurement_type' ? 'bg-blue! text-white! border-blue!' : 'border-blue! text-blue!',
                  procurementTypesStore.activeProcurementTypes.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('procurement_type')"
              >
                {{ activeTaxonomy === 'procurement_type' ? '-' : '+' }}
              </button>
            </div>

            <div
              class="inline-flex! items-center! gap-1.5! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-blue!"
              :class="businessModelTypologiesStore.activeBusinessModelTypologies.length > 0 ? 'font-semibold!' : 'font-medium!'"
            >
              <span>
                {{ strings.business_model_typology }}
                <span v-if="businessModelTypologiesStore.activeBusinessModelTypologies.length > 0">[{{ businessModelTypologiesStore.activeBusinessModelTypologies.length }}]</span>
              </span>
              <button
                class="w-4.5! h-4.5! text-xl! leading-none! flex! items-center! justify-center! shrink-0! border!"
                :class="[
                  activeTaxonomy === 'business_model_typology' ? 'bg-blue! text-white! border-blue!' : ' border-blue! text-blue!',
                  businessModelTypologiesStore.activeBusinessModelTypologies.length > 0 ? 'border-2! font-bold!' : ''
                ]"
                @click="toggleTaxonomy('business_model_typology')"
              >
                {{ activeTaxonomy === 'business_model_typology' ? '-' : '+' }}
              </button>
            </div>
          </div>

          <div v-if="activeTaxonomy === 'country'" class="flex! flex-wrap! items-center! gap-2! bg-light-blue-2! p-6!">
            <button
              v-for="country in countriesStore.countries"
              :key="`mobile-country-${country.id}`"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-2.5! py-0.5! border! border-black! rounded-full!"
              :class="countriesStore.activeCountries.includes(country.code) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="countriesStore.toggleCountry(country.code)"
            >
              {{ country.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'city'" class="flex! flex-wrap! items-center! gap-2! bg-light-blue-2! px-6! py-4!">
            <button
              v-for="city in citiesStore.cities"
              :key="`mobile-city-${city.id}`"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-2.5! py-0.5! border! border-black! rounded-full!"
              :class="citiesStore.activeCities.includes(city.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="citiesStore.toggleCity(city.slug)"
            >
              {{ city.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'city_population_range'" class="flex! flex-wrap! items-center! gap-2! bg-light-blue-2! px-6! py-4!">
            <button
              v-for="range in cityPopulationRangesStore.ranges"
              :key="`mobile-range-${range.slug}`"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-2.5! py-0.5! border! border-black! rounded-full!"
              :class="cityPopulationRangesStore.activeRanges.includes(range.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="cityPopulationRangesStore.toggleRange(range.slug)"
            >
              {{ range.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'procurement_type'" class="flex! flex-wrap! items-center! gap-2! bg-light-blue-2! px-6! py-4!">
            <button
              v-for="procurementType in procurementTypesStore.procurementTypes"
              :key="`mobile-procurement-type-${procurementType.id}`"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-2.5! py-0.5! border! border-black! rounded-full!"
              :class="procurementTypesStore.activeProcurementTypes.includes(procurementType.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="procurementTypesStore.toggleProcurementType(procurementType.slug)"
            >
              {{ procurementType.name }}
            </button>
          </div>

          <div v-if="activeTaxonomy === 'business_model_typology'" class="flex! flex-wrap! items-center! gap-2! bg-light-blue-2! px-6! py-4!">
            <button
              v-for="businessModelTypology in businessModelTypologiesStore.businessModelTypologies"
              :key="`mobile-business-model-${businessModelTypology.id}`"
              class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-2.5! py-0.5! border! border-black! rounded-full!"
              :class="businessModelTypologiesStore.activeBusinessModelTypologies.includes(businessModelTypology.slug) ? 'bg-blue! border-blue! text-white!' : ''"
              @click="businessModelTypologiesStore.toggleBusinessModelTypology(businessModelTypology.slug)"
            >
              {{ businessModelTypology.name }}
            </button>
          </div>
        </div>

        <div v-if="isMobileSortOpen" class="flex! flex-col! gap-y-4! bg-light-blue-3! -mx-6! px-6! py-4!">
          <template v-for="header in mobileSortableHeaders" :key="`mobile-sort-group-${header.key}`">
            <div class="inline-flex! items-center! gap-4!">
              <component v-if="header.icon" :is="header.icon" class="w-5! h-5! shrink-0!" />
              <span class="font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black! capitalize!">
                {{ header.label }}
              </span>
              <button
                type="button"
                class="w-5! h-5! flex! items-center! justify-center! border!"
                :class="sort.field === header.key && sort.order === 'desc' ? 'bg-blue! border-blue! [&>svg_path]:stroke-white!' : 'border-blue! [&>svg_path]:stroke-blue!'"
                @click="setSort(header.key, 'desc')"
              >
                <ArrowIcon class="w-1.5! h-2.5!" />
              </button>
            </div>

            <div class="inline-flex! items-center! gap-4!">
              <component v-if="header.icon" :is="header.icon" class="w-5! h-5! shrink-0!" />
              <span class="font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black! capitalize!">
                {{ header.label }}
              </span>
              <button
                type="button"
                class="w-5! h-5! flex! items-center! justify-center! border!"
                :class="sort.field === header.key && sort.order === 'asc' ? 'bg-blue! border-blue! [&>svg_path]:stroke-white!' : 'border-blue! [&>svg_path]:stroke-blue!'"
                @click="setSort(header.key, 'asc')"
              >
                <ArrowIcon class="w-1.5! h-2.5! rotate-180!" />
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="flex! flex-col! gap-y-6!">
      <div v-if="isLoading" class="px-6! py-12! text-center! flex! items-center! justify-center!">
        <span class="w-12! h-12! border-4! border-blue! border-t-transparent! rounded-full! animate-spin!"></span>
      </div>

      <div v-else-if="sortedProcurements.length === 0" class="px-6! py-12! text-center!">
        <span class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
          {{ strings.no_procurements_match_filters }}
        </span>
      </div>

      <div v-else class="hidden lg:block">
        <table class="w-full! border-collapse! bg-light-blue-3!">
          <thead>
            <draggable
              :model-value="headers"
              tag="tr"
              item-key="key"
              @update:model-value="headers = $event"
            >
              <template #item="{ element: header }">
                <th class="font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black! text-left! capitalize! pt-4! pb-2! pr-4!">
                  <div class="flex! items-center! gap-x-2.5!">
                    <span
                      v-if="header.key === 'id'"
                      class="text-2xl! text-blue!"
                    >
                      #
                    </span>
                    <component
                      v-if="header.icon"
                      :is="header.icon"
                      class="w-5! h-4!"
                    />
                    <span :class="header.key === 'id' ? 'uppercase!' : 'capitalize!'">{{ header.label }}</span>

                    <div v-if="header.sortable" class="flex! gap-1.25!">
                      <button
                        class="flex! items-center! justify-center! py-1! px-2! border!"
                        :class="sort.field === header.key && sort.order === 'desc' ? 'bg-blue! border-blue! [&>svg_path]:stroke-white!' : 'border-blue! [&>svg_path]:stroke-blue!'"
                        @click="setSort(header.key, 'desc')"
                      >
                        <ArrowIcon class="w-1.5! h-2.5!" />
                      </button>
                      <button
                        class="flex! items-center! justify-center! py-1! px-2! border!"
                        :class="sort.field === header.key && sort.order === 'asc' ? 'bg-blue! border-blue! [&>svg_path]:stroke-white!' : 'border-blue! [&>svg_path]:stroke-blue!'"
                        @click="setSort(header.key, 'asc')"
                      >
                        <ArrowIcon class="w-1.5! h-2.5! rotate-180!" />
                      </button>
                    </div>
                  </div>
                </th>
              </template>
            </draggable>
          </thead>

          <tbody>
            <tr
              v-for="(procurement, rowIndex) in paginatedProcurements"
              :key="getProcurementId(procurement) ?? rowIndex"
              :style="rowIndex % 2 === 0 ? 'background-color: #BBCBE5;' : 'background-color: #C6D8F2;'"
            >
              <td
                v-for="header in headers"
                :key="`${getProcurementId(procurement) ?? rowIndex}-${header.key}`"
                class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-left! text-black! py-4.5! pr-5!"
                :class="header.key === 'select' ? 'text-center! align-middle!' : ''"
              >
                <input
                  v-if="header.key === 'select'"
                  v-model="selectedProcurements"
                  :value="getProcurementId(procurement)"
                  type="checkbox"
                  class="appearance-none! w-4.25! h-4.25! rounded-full! border! border-black! bg-transparent! checked:bg-blue! checked:border-blue! cursor-pointer! block! mx-auto!"
                />

                <span v-else>
                  <span
                    v-if="header.key === 'procurement_type' || header.key === 'business_model_typology'"
                    class="inline-flex! items-center! gap-2.5!"
                  >
                    <span
                      v-if="getProcurementDescription(procurement, header.key)"
                      class="relative! inline-flex! items-center! group"
                    >
                      <InfoIcon class="w-4! h-4!" />
                      <span
                        class="pointer-events-none! absolute! left-1/2! top-full! z-50! mt-1! w-85.5! -translate-x-1/2! bg-blue! px-4! py-3! font-ibm-plex-mono! font-normal text-base! leading-tight! text-white! opacity-0! transition-opacity! group-hover:opacity-100!"
                      >
                        {{ getProcurementDescription(procurement, header.key) }}
                      </span>
                    </span>

                    <span>{{ getProcurementValue(procurement, header.key) }}</span>
                  </span>

                  <span v-else>
                    {{ getProcurementValue(procurement, header.key) }}
                  </span>
                </span>
              </td>

              <td class="inline-block! py-4.5!">
                <a
                  :href="getProcurementUrl(procurement)"
                  class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! lowercase! px-3.5! py-0.5! border! border-black! rounded-full! no-underline!"
                >
                  {{ strings.details }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && sortedProcurements.length > 0" class="lg:hidden! flex! flex-col!">
        <div
          v-for="(procurement, rowIndex) in paginatedProcurements"
          :key="`mobile-${getProcurementId(procurement) ?? rowIndex}`"
          class="flex! flex-col!"
        >
          <div class="flex! items-center! justify-between! gap-2! p-6! bg-blue/10!">
            <label class="flex! items-center! gap-2! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black!">
              <input
                v-model="selectedProcurements"
                :value="getProcurementId(procurement)"
                type="checkbox"
                class="appearance-none! w-4! h-4! rounded-full! border! border-black! bg-transparent! checked:bg-blue! checked:border-blue! cursor-pointer!"
              />
              #{{ getProcurementId(procurement) }}
            </label>

            <a
              :href="getProcurementUrl(procurement)"
              class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! lowercase! border! border-black! rounded-full! px-3.5! py-0.5! no-underline!"
            >
              {{ strings.details }}
            </a>
          </div>

          <div
            class="flex! flex-col! gap-y-4! p-6!"
            :style="rowIndex % 2 === 0 ? 'background-color: #BBCBE5;' : 'background-color: #C6D8F2;'"
          >
            <div
              v-for="header in mobileCardHeaders"
              :key="`mobile-${getProcurementId(procurement) ?? rowIndex}-${header.key}`"
              class="flex! flex-col! gap-y-0.5!"
            >
              <div class="relative! flex! items-center! gap-2.5!">
                <component v-if="header.icon" :is="header.icon" class="w-5! h-5! shrink-0!" />

                <span class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-dark-blue! capitalize!">
                  {{ header.label }}
                </span>

                <span
                  v-if="(header.key === 'procurement_type' || header.key === 'business_model_typology') && getProcurementDescription(procurement, header.key)"
                  class="inline-flex! items-center!"
                >
                  <button
                    type="button"
                    class="inline-flex! items-center! p-0! border-0! bg-transparent!"
                    @click.stop="toggleMobileInfoTooltip(`${getProcurementId(procurement) ?? rowIndex}-${header.key}`)"
                  >
                    <InfoIcon class="w-3.5! h-3.5!" />
                  </button>
                </span>

                <div
                  v-if="openMobileInfoTooltip === `${getProcurementId(procurement) ?? rowIndex}-${header.key}`"
                  class="absolute! left-1/2! top-full! z-50! mt-1! w-82! max-w-[calc(100vw-4rem)]! -translate-x-1/2! bg-blue! px-3! py-2! font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-white! break-words!"
                >
                  {{ getProcurementDescription(procurement, header.key) }}
                </div>
              </div>

              <span class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! pl-7.5! break-words!">
                {{ getProcurementValue(procurement, header.key) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && sortedProcurements.length > 0" class="flex! flex-col! lg:flex-row! lg:items-center! lg:justify-between! gap-3!">
      <a
        :href="selectedProcurements.length > 0 ? withLang(comparisonPageUrl) + '?ids=' + selectedIds : '#'"
        class="self-start! font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-blue! lowercase! border! border-blue! rounded-full! px-3.5! py-0.5! no-underline!"
        :class="selectedProcurements.length === 0 ? 'pointer-events-none! opacity-50!' : ''"
      >
        {{ strings.compare_selected_procurements }}
      </a>

      <div class="w-full! lg:w-auto! lg:ml-auto! flex! items-center! justify-end! gap-2!">
        <button
          type="button"
          class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! lowercase! border! border-black! rounded-full! px-3.5! py-0.5!"
          :class="!canGoPreviousPage ? 'opacity-40! cursor-not-allowed!' : ''"
          :disabled="!canGoPreviousPage"
          @click="goToPreviousPage"
        >
          {{ strings.previous }}
        </button>

        <button
          type="button"
          class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! lowercase! border! border-black! rounded-full! px-3.5! py-0.5!"
          :class="!canGoNextPage ? 'opacity-40! cursor-not-allowed!' : ''"
          :disabled="!canGoNextPage"
          @click="goToNextPage"
        >
          {{ strings.next }}
        </button>
      </div>
    </div>

    <div class="mt-auto! pt-10! flex! justify-end!">
      <div class="flex! items-center! gap-x-5!">
        <router-link to="/references" class="font-ibm-plex-mono! font-medium! text-black! text-lg! leading-tight! no-underline!">
          {{ strings.references }}
        </router-link>
      </div>
    </div>
  </section>
</template>
